import { get, set } from 'lodash';
import type { CostingTemplateColumn, NamedRef } from './store/costingTemplate.types';
import type { BombiixColumnDef, BombiixRow } from '../../../Components/BombiixAGGrid/types';

/**
 * Pure logic shared by the Costing Template screens.
 *
 * Extracted from the JavaScript module, where each of these sat module-private inside the component
 * file that used it. No React, no redux, no I/O — the same code runs in the pages and in the tests.
 */

/**
 * Reconciles the rows the server returned with the unsaved drafts already on screen.
 *
 * A draft is dropped once the server echoes a row with the same title, because that means the save
 * landed and the saved row now represents it — matching on trimmed, case-insensitive title rather
 * than on id, since a draft has no id to match with.
 *
 * @param serverRows rows as last loaded from the costing template.
 * @param currentRows rows currently rendered, saved and draft mixed.
 * @returns the saved rows followed by the drafts that are still genuinely unsaved.
 */
export function mergeDraftRows(serverRows: CostingTemplateColumn[] | undefined, currentRows: CostingTemplateColumn[] | undefined): CostingTemplateColumn[] {
  const savedRows = Array.isArray(serverRows) ? serverRows : [];
  const savedTitles = new Set(savedRows.map((row) => (row.title || '').trim().toLowerCase()).filter(Boolean));
  const drafts = (Array.isArray(currentRows) ? currentRows : []).filter((row) => !row._id && !(row.title && savedTitles.has(row.title.trim().toLowerCase())));
  return [...savedRows, ...drafts];
}

/** One row's error flags. Sparse: only rows with an error have an entry. */
export interface TitleErrorEntry {
  title?: boolean | string;
  [key: string]: unknown;
}

/** What a duplicate-title scan found. */
export interface DuplicateTitleResult {
  errors: TitleErrorEntry[];
  names: string[];
  hasError: boolean;
}

/**
 * Flags every row whose trimmed, case-insensitive title collides with another row's in the same list.
 *
 * Both the first occurrence and the repeat are flagged, so the conflicting pair is highlighted
 * rather than just the later one. Blank titles are skipped — a row the user has not filled in yet is
 * a required-field problem, not a duplicate.
 *
 * @param rows the rows of one costing-template section.
 * @returns a sparse error array, the offending titles, and whether anything collided.
 */
export const collectDuplicateTitleErrors = (rows: CostingTemplateColumn[] | undefined): DuplicateTitleResult => {
  const seenIndexByName = new Map<string, number>();
  const errors: TitleErrorEntry[] = [];
  const names = new Set<string>();
  (rows ?? []).forEach((row, index) => {
    const rawTitle = (get(row, 'title', '') as string) || '';
    const normalised = rawTitle.trim().toLowerCase();
    if (!normalised) return;
    if (seenIndexByName.has(normalised)) {
      set(errors, `[${index}].title`, true);
      set(errors, `[${seenIndexByName.get(normalised)}].title`, true);
      names.add(rawTitle.trim());
    } else {
      seenIndexByName.set(normalised, index);
    }
  });
  return { errors, names: [...names], hasError: names.size > 0 };
};

/**
 * Overlays duplicate-title flags onto an existing required-field error array.
 *
 * One error array per table then carries both borders. An existing message wins over the duplicate
 * flag, so a row that is both blank and duplicated still reports the blank.
 *
 * @param baseErrors the required-field errors already computed.
 * @param duplicateErrors the sparse array from `collectDuplicateTitleErrors`.
 * @returns the merged error array.
 */
/**
 * Flags every row whose title is blank — saved rows included.
 *
 * The four original sections skip a row once it carries an `_id`, because there a saved row can only
 * exist if it already passed that section's own per-row save. Specification Requirements (BM-9899)
 * has no per-row save and leaves saved rows editable, so the page's single Save rewrites its whole
 * array: a user can blank an already-saved requirement, and skipping `_id` rows would write that
 * empty value straight back.
 *
 * @param rows every row currently on screen for the section.
 * @returns whether any row is blank, and the sparse per-row error flags.
 */
export const collectBlankTitleErrors = (rows: CostingTemplateColumn[] | undefined): { hasError: boolean; errors: TitleErrorEntry[] } => {
  const errors: TitleErrorEntry[] = [];
  let hasError = false;
  (rows ?? []).forEach((row, index) => {
    if (!(get(row, 'title', '') as string).trim()) {
      hasError = true;
      set(errors, `[${index}].title`, true);
    }
  });
  return { hasError, errors };
};

export const mergeTitleErrors = (baseErrors: TitleErrorEntry[] | undefined, duplicateErrors: TitleErrorEntry[] | undefined): TitleErrorEntry[] => {
  const merged: TitleErrorEntry[] = Array.isArray(baseErrors) ? [...baseErrors] : [];
  (duplicateErrors ?? []).forEach((entry, index) => {
    if (entry?.title) {
      merged[index] = { ...(merged[index] || {}), title: merged[index]?.title || entry.title };
    }
  });
  return merged;
};

/**
 * Stable cache key for a set of selected brand ids.
 *
 * Sorted, so the same brands picked in a different order hit the same cached sub-brand options.
 *
 * @param brandIds the selected brand ids.
 * @returns a comma-joined key, or an empty string when nothing is selected.
 */
export const buildBrandKey = (brandIds: unknown[] | undefined): string => (Array.isArray(brandIds) ? [...brandIds].map(String).sort().join(',') : '');

/**
 * Comma-separated names, for read-only rows whose value is a populated `[{ _id, name }]`.
 * @param items the populated references.
 * @returns the joined names, or an empty string.
 */
export const joinNames = (items: NamedRef[] | undefined): string =>
  Array.isArray(items)
    ? items
        .map((item) => item?.name)
        .filter(Boolean)
        .join(', ')
    : '';

/** One parent axis a child option must match on. */
export interface ParentSelection {
  optKey: string;
  selectedIds: string[];
}

/**
 * Filters child-category options against the parent selections on the same draft row.
 *
 * An option survives only when *every* parent axis has at least one hit, and the whole list is
 * emptied the moment any parent has nothing selected — a child category is meaningless without its
 * parents chosen.
 *
 * @param optionList the unfiltered child options.
 * @param parentSelections the parent axes to match against.
 * @returns the options valid for the current parent selection.
 */
export const filterByParents = (optionList: NamedRef[] | undefined, parentSelections: ParentSelection[]): NamedRef[] => {
  if (parentSelections.some(({ selectedIds }) => !selectedIds?.length)) return [];
  return (Array.isArray(optionList) ? optionList : []).filter((option) =>
    parentSelections.every(({ optKey, selectedIds }) => {
      const raw = option?.[optKey];
      const optionParentIds = Array.isArray(raw) ? raw : [raw];
      return optionParentIds.some((parentId) => selectedIds.includes(String((parentId as NamedRef)?._id || parentId)));
    }),
  );
};

/** Scope fields in parent-before-child order, so a filled parent resets its children before they are filled. */
export const TEMPLATE_SCOPE_FILL_ORDER: readonly string[] = ['brandId', 'subBrandId', 'productTypeId', 'catOneId', 'catTwoId', 'catThreeId', 'catFourId'];

/**
 * Clears the child selections a changed scope field invalidates, so stale child ids never persist.
 *
 * @param row the draft row after the change.
 * @param field the scope field that changed.
 * @returns the row with its dependent selections emptied.
 */
export const resetDependentScope = <TRow extends Record<string, unknown>>(row: TRow, field: string): TRow => {
  if (field === 'brandId') return { ...row, subBrandId: [] };
  if (field === 'catOneId' || field === 'catTwoId') return { ...row, catThreeId: [], catFourId: [] };
  if (field === 'catThreeId') return { ...row, catFourId: [] };
  return row;
};

/**
 * The ids that are options of the target row; a filled sub-brand or category only stays when the
 * row's own parents offer it.
 *
 * @param ids the ids being written.
 * @param options the options the target row offers, or undefined when the column has none.
 * @returns the ids that exist among the options.
 */
export const keepOptionIds = (ids: string[], options: NamedRef[] | undefined): string[] => {
  const allowed = new Set((options ?? []).map((option) => String(option?._id)));
  return ids.filter((id) => allowed.has(id));
};

/**
 * The options a scope column offers one row: its static options overlaid with its per-row ones.
 *
 * @param columnDefs the grid's columns.
 * @param field the scope field.
 * @param row the target row.
 * @returns the row's options, or undefined when the grid has no such column.
 */
export const optionsOfColumn = <TRow extends BombiixRow>(columnDefs: BombiixColumnDef<TRow>[], field: string, row: TRow): NamedRef[] | undefined => {
  const column = columnDefs.find((col) => col.field === field);
  if (!column) return undefined;
  const props = { ...(column.cellProps ?? {}), ...(column.getCellProps?.(row) ?? {}) } as { options?: NamedRef[] };
  return props.options;
};

/**
 * The draft rows as they were before a fill, keyed by draft id. AG Grid writes each filled value into
 * the draft object before the fill is handled, so every filled field is put back to the value it
 * held; a refused cell then gets back that value, or the empty value a filled parent's reset left it.
 *
 * @param drafts the draft rows.
 * @param batch the filled cells, each with the value it held before the fill.
 * @returns the rows, keyed by draft id.
 */
export const draftsBeforeFill = <TRow extends { __draftId?: string }>(drafts: TRow[], batch: Array<{ rowId: unknown; field: string; oldValue: unknown }>): Map<string, TRow> => {
  const rowsById = new Map(drafts.map((draft) => [String(draft.__draftId), draft]));
  batch.forEach(({ rowId, field, oldValue }) => {
    const draft = rowsById.get(String(rowId));
    if (draft) rowsById.set(String(rowId), { ...draft, [field]: oldValue });
  });
  return rowsById;
};
